import { Button } from "design-system/atoms/Button"
import { Checkbox } from "design-system/atoms/Checkbox"
import { Input } from "design-system/atoms/Input"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import { Command, CommandGroup, CommandItem, CommandList } from "design-system/molecules/Command"
import { Section } from "design-system/molecules/Section"
import { ChevronDown, Search } from "lucide-react"
import { useGetCampaignHealthQuery } from "modules/backoffice/hooks/campaigns/useGetCampaignHealthQuery"
import { ORGANIZATION_TYPE_TRANSLATIONS } from "modules/backoffice/lib/organizations/translations"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { OrganizationType, OrganizationTypeEnum } from "schema"
import { BackofficeCampaignHealthTable } from "./BackofficeCampaignHealthTable"

export const BackofficeCampaignHealth = () => {
  const { t } = useTranslation()
  const [search, setSearch] = useState("")
  const [selectedTypes, setSelectedTypes] = useState<OrganizationType[]>([
    OrganizationTypeEnum.Client,
    OrganizationTypeEnum.Partner,
  ])

  const trimmedSearch = search.trim()
  const hasMinSearch = trimmedSearch.length >= 3

  const { data, isPending } = useGetCampaignHealthQuery(
    {
      organizationTypes: selectedTypes,
      search: trimmedSearch || undefined,
    },
    { enabled: hasMinSearch },
  )

  function toggleType(value: OrganizationType | "all") {
    if (value === "all") return setSelectedTypes([])
    setSelectedTypes((prev) => {
      return prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    })
  }

  const typeOptions = useMemo(() => {
    return [
      {
        value: "all",
        label: t("backoffice.campaigns.health.filters.allOrganizationTypes"),
      },
      {
        value: OrganizationTypeEnum.Client,
        label: t(ORGANIZATION_TYPE_TRANSLATIONS[OrganizationTypeEnum.Client]),
      },
      {
        value: OrganizationTypeEnum.Partner,
        label: t(ORGANIZATION_TYPE_TRANSLATIONS[OrganizationTypeEnum.Partner]),
      },
      {
        value: OrganizationTypeEnum.Freemium,
        label: t(ORGANIZATION_TYPE_TRANSLATIONS[OrganizationTypeEnum.Freemium]),
      },
      {
        value: OrganizationTypeEnum.Integration,
        label: t(ORGANIZATION_TYPE_TRANSLATIONS[OrganizationTypeEnum.Integration]),
      },
    ]
  }, [t])

  const selectedLabel = useMemo(() => {
    if (selectedTypes.length === 0) {
      return t("backoffice.campaigns.health.filters.allOrganizationTypes")
    }

    const labelMap = new Map(typeOptions.map((o) => [o.value, o.label]))
    return selectedTypes.map((v) => labelMap.get(v)).join(", ")
  }, [selectedTypes, typeOptions, t])

  return (
    <div className="space-y-6 w-full">
      <Section
        title={t("backoffice.campaigns.health.title")}
        description={t("backoffice.campaigns.health.description")}
      />
      <div className="flex flex-col gap-4 w-full">
        <div className="flex flex-col sm:flex-row gap-2 w-full">
          <Input
            icon={Search}
            placeholder={t("backoffice.campaigns.health.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full"
          />
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full sm:w-auto min-w-40 h-full justify-between"
              >
                <span className="truncate text-left">{selectedLabel}</span>
                <ChevronDown className="h-5 w-5 shrink-0" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 min-w-56" align="start">
              <Command>
                <CommandList>
                  <CommandGroup>
                    {typeOptions.map((type) => {
                      const checked =
                        type.value === "all"
                          ? selectedTypes.length === 0
                          : selectedTypes.includes(type.value as OrganizationType)
                      return (
                        <CommandItem
                          key={type.value}
                          value={String(type.value)}
                          onSelect={() => toggleType(type.value as OrganizationType | "all")}
                          className="gap-2"
                        >
                          <Checkbox checked={checked} />
                          <span className="truncate">{type.label}</span>
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <BackofficeCampaignHealthTable
          data={hasMinSearch ? data : undefined}
          isPending={hasMinSearch ? isPending : false}
          showSearchPrompt={!hasMinSearch}
          searchPromptMessage={t("backoffice.campaigns.health.searchPrompt")}
        />
      </div>
    </div>
  )
}
